/**
 * AppUpdateBanner — production SW update prompt
 */

import { useEffect, useRef, useState } from "react";
import { ArrowsClockwise, X, Warning } from "@phosphor-icons/react";

const AUTO_RELOAD_MS  = 15_000;
const TICK_MS         = 1_000;

export default function AppUpdateBanner() {
  const [visible, setVisible]         = useState(false);
  const [countdown, setCountdown]     = useState(Math.round(AUTO_RELOAD_MS / 1000));
  const [applying, setApplying]       = useState(false);
  const registrationRef               = useRef(null);
  const timerRef                      = useRef(null);
  const countdownRef                  = useRef(null);

  useEffect(() => {
    const onUpdateAvailable = (e) => {
      registrationRef.current = e.detail?.registration ?? null;
      setVisible(true);
      setCountdown(Math.round(AUTO_RELOAD_MS / 1000));

      countdownRef.current = setInterval(() => {
        setCountdown((n) => {
          if (n <= 1) {
            clearInterval(countdownRef.current);
            applyUpdate(registrationRef.current);
            return 0;
          }
          return n - 1;
        });
      }, TICK_MS);
    };

    window.addEventListener("sw:update-available", onUpdateAvailable);
    return () => {
      window.removeEventListener("sw:update-available", onUpdateAvailable);
      clearInterval(countdownRef.current); // eslint-disable-line react-hooks/exhaustive-deps
      clearTimeout(timerRef.current);      // eslint-disable-line react-hooks/exhaustive-deps
    };
  }, []);

  const applyUpdate = (reg) => {
    setApplying(true);
    clearInterval(countdownRef.current);
    const sw = reg?.waiting;
    if (sw) {
      sw.postMessage({ type: "SKIP_WAITING" });
    } else {
      window.location.reload();
    }
  };

  const handleRefresh = () => applyUpdate(registrationRef.current);
  const handleDismiss = () => {
    clearInterval(countdownRef.current);
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label="Application update available"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100vw-2rem)] max-w-lg"
    >
      <div className="bg-white border border-blue-200 shadow-xl rounded-xl px-5 py-4 flex items-center gap-4">
        <div className="w-9 h-9 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center shrink-0">
          <Warning size={18} weight="fill" className="text-blue-600" aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-900 leading-tight">
            New version available
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {applying
              ? "Applying update…"
              : `Auto-refreshing in ${countdown}s — or refresh now.`}
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={applying}
          className="shrink-0 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2 px-4 text-xs uppercase tracking-wider transition-colors rounded-lg"
          aria-label="Refresh to apply update"
        >
          <ArrowsClockwise
            size={13}
            weight="bold"
            className={applying ? "animate-spin" : ""}
            aria-hidden="true"
          />
          {applying ? "…" : "Refresh"}
        </button>

        {!applying && (
          <button
            onClick={handleDismiss}
            className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors p-1"
            aria-label="Dismiss update notification"
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {!applying && (
        <div className="h-1 bg-slate-100 w-full rounded-b-xl overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
            style={{ width: `${(countdown / Math.round(AUTO_RELOAD_MS / 1000)) * 100}%` }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
